import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message } from "@mimber/ui-web"
import {
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@mimber/ui-web"
import { useInvoices } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { InvoiceWithRelationsDto } from "@mimber/schema"
import { InvoiceStatus, InvoiceSourceType } from "@mimber/schema"
import { formatCurrency } from "@/utils"
import type { InvoicesTableProps } from "./InvoicesTable.types"
import { SOURCE_LABELS, STATUS_COLORS } from "./InvoicesTable.utils"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const InvoicesTable: React.FunctionComponent<InvoicesTableProps> = ({
  onRowClick,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "invoices",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data: response, isLoading, error } = useInvoices(queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load invoices: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<InvoiceWithRelationsDto> = useMemo(
    () => [
      {
        title: "Invoice #",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnSearchProps("Search invoice number"),
      },
      {
        title: "Source",
        dataIndex: "sourceType",
        key: "sourceType",
        render: (sourceType: InvoiceSourceType | null) =>
          sourceType ? (
            <Text>{SOURCE_LABELS[sourceType] ?? sourceType}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
        filters: Object.values(InvoiceSourceType).map((type) => ({
          text: SOURCE_LABELS[type] ?? type,
          value: type,
        })),
      },
      {
        title: "Member",
        key: "member",
        render: (_: unknown, record: InvoiceWithRelationsDto) => {
          const user = record.membership?.user
          if (!user) return <Text type="secondary">—</Text>
          return (
            <Space>
              <UserOutlined />
              <Text>
                {user.firstName} {user.lastName}
              </Text>
            </Space>
          )
        },
      },
      {
        title: "Amount",
        key: "totalAmount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (_: unknown, record: InvoiceWithRelationsDto) => (
          <Space>
            <DollarOutlined />
            <Text>{formatCurrency(record.totalAmount)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: InvoiceStatus) => (
          <Tag color={STATUS_COLORS[status] ?? "default"}>{status}</Tag>
        ),
        filters: Object.values(InvoiceStatus).map((status) => ({
          text: status,
          value: status,
        })),
      },
      {
        title: "Due Date",
        dataIndex: "dueAt",
        key: "dueAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (dueAt: string | null) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(dueAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Issued",
        dataIndex: "issuedAt",
        key: "issuedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (issuedAt: string | null) => (
          <Text type="secondary">{formatTableDate(issuedAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<InvoiceWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<InvoiceWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
